// src/app/TutorChatMockup.tsx
//
// Decorative hero mockup of the AI Tutor chat. Shown on the landing
// page in place of a "real student data" graph — pre-launch we have
// no real users to plot, so we lead with a product-UI mockup that
// honestly shows what the headline differentiator (AI Tutor) does
// rather than implying outcomes we can't back up. See PR #428 for
// the labeling fix on the Score Trend chart that preceded this.
//
// This component is intentionally static — no interactivity. The
// "Type a question..." footer is purely visual (a faux input that
// doesn't accept focus). The chat content was hand-written to:
//
//   - Show CONTEXT-AWARENESS ("looking at your last 5 attempts")
//     because that's what the AI Tutor actually does and it's the
//     real differentiator vs ChatGPT or a static FAQ.
//   - Show a SPECIFIC INSIGHT (the exponent-as-coefficient
//     misconception — students reading x² as 2x) rather than a
//     generic "I help you study!" — credibility comes from
//     specifics. The misconception MUST be one where the wrong
//     mental model produces a clearly wrong numeric answer for
//     the worked example, otherwise the diagnosis doesn't hold
//     up: a previous draft used f(3) with f(x) = 2x + 5, but a
//     student who reads f(3) as "multiply by 3" would still
//     land on the correct answer 11 (the function-evaluation
//     step is invariant to that misread). x² → 2x with the
//     example f(x) = x² + 1, find f(4) gives 17 correct vs 9
//     wrong — clean diagnostic separation.
//   - Show a CLOSED LOOP (student answers, tutor confirms + offers
//     more practice) — that's the diagnose → drill → reinforce
//     cycle which is the product, not a one-shot chatbot.
//   - Stay readable in a hero glance — every bubble is short.
//
// If you swap the copy, keep all four properties or the marketing
// value drops.

import { Sparkles, Bot } from 'lucide-react'

export default function TutorChatMockup() {
  return (
    <div className="flex flex-col gap-3" aria-label="AI Tutor chat preview">
      {/* Student message — right-aligned, brand-cyan tinted bubble. */}
      <div className="flex justify-end gap-2">
        <div className="max-w-[85%] rounded-2xl rounded-tr-sm bg-brand/15 px-3 py-2 text-sm leading-relaxed text-foreground">
          Why did I get the Function questions wrong on the last quiz?
        </div>
        <StudentAvatar />
      </div>

      {/* Tutor response — left-aligned, neutral bubble with a bot
          avatar to underline "this is the AI, not a human tutor".
          The misconception called out here is the exponent-as-
          coefficient error (x² read as 2x). The worked example is
          chosen so the misread produces a clearly different answer
          from the correct one: f(4) = 4² + 1 = 17 if you understand
          exponents, but a student who computes "2·4 + 1" lands on
          9. The wrong answer must differ from the right one or the
          diagnostic story doesn't hold up. */}
      <div className="flex gap-2">
        <TutorAvatar />
        <div className="max-w-[85%] rounded-2xl rounded-tl-sm border border-border bg-card px-3 py-2 text-sm leading-relaxed text-foreground">
          <p>
            Looking at your last 5 attempts, you&apos;re reading{' '}
            <span className="rounded bg-brand/10 px-1 font-mono text-brand-ink">x²</span> as
            &ldquo;2x&rdquo; — but x² means <strong>x times itself</strong> (x · x). Try this one:
          </p>
          <div className="mt-2 rounded-md border border-border bg-background/50 p-2 font-mono text-xs text-foreground">
            If f(x) = x² + 1, what is f(4)?
          </div>
        </div>
      </div>

      {/* Student follow-up — short answer to the tutor's mini-problem.
          This second turn does the heavy lifting of the mockup: it
          shows the closed-loop (tutor poses → student answers → tutor
          confirms + escalates to more practice), which is what makes
          this an actual tutor vs. a one-shot Q&A. Also fills the
          vertical space in the hero card so the layout doesn't look
          empty under the caption. */}
      <div className="flex justify-end gap-2">
        <div className="max-w-[85%] rounded-2xl rounded-tr-sm bg-brand/15 px-3 py-2 text-sm leading-relaxed text-foreground">
          <span className="font-mono">17</span>
        </div>
        <StudentAvatar />
      </div>

      {/* Tutor confirmation + proactive next-step. "Want me to generate
          3 more like this?" is the differentiating behaviour — the
          tutor reinforces the concept by drilling, not by lecturing.
          Keep this bubble short; the marketing payload is the
          "generate more like this" hook, not the praise. */}
      <div className="flex gap-2">
        <TutorAvatar />
        <div className="max-w-[85%] rounded-2xl rounded-tl-sm border border-border bg-card px-3 py-2 text-sm leading-relaxed text-foreground">
          Exactly. Want me to <strong>generate 3 more like this</strong> so it sticks?
        </div>
      </div>

      {/* Decorative input. role/aria are deliberately absent so screen
          readers don't announce it as a real form field. */}
      <div className="mt-2 flex items-center gap-2 rounded-full border border-border bg-card/40 px-4 py-2 text-sm text-muted-foreground">
        <Sparkles className="h-4 w-4 text-brand-ink" />
        <span className="flex-1">Ask the tutor anything…</span>
      </div>
    </div>
  )
}

function StudentAvatar() {
  return (
    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand/20 text-[11px] font-semibold text-brand-ink">
      You
    </div>
  )
}

function TutorAvatar() {
  return (
    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand/20 text-brand-ink">
      <Bot className="h-4 w-4" aria-hidden />
    </div>
  )
}
